import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Session lives in an httpOnly cookie; ask the server who we are.
    api
      .me()
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    // Backend sets the httpOnly auth cookie; response body carries the user.
    const res = await api.login(email, password);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout(); // clears the server-side auth cookie
    } catch (e) {
      // Non-fatal: the server-side cookie clear failed (offline/expired), but we
      // still clear client state below so the user is logged out locally.
      console.warn("Logout request failed; clearing client session anyway", e);
    }
    setUser(null);
    window.location.href = "/login";
  }, []);

  const role = user?.role || null;
  const value = {
    user,
    setUser,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: role === "admin",
    isWriter: role === "admin" || role === "member", // can perform write actions
    isViewer: role === "viewer",
    role,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
